import { useContext } from 'react';

import FavoritesIconButton from '@atom/components/common/favorites/FavoritesIconButton';
import { Checkbox, Icon, List } from '@atom/mui';
import { ArrowDropDownIcon, ArrowRightIcon } from '@atom/mui/icons';
import { CategoryPathItem } from '@atom/types/category';

import { isAsset } from './InventoryAssetTree';
import TreeContext, { Tree } from './TreeContext';
import TreeRowIcon from './TreeRowIcon';

const { ListItemButton, ListItemText } = List;

export interface Props {
  isAncestorSelected?: boolean;
  hasChildren: boolean;
  hasCategories: boolean;
  level: number;
  tree: Tree;
  path: CategoryPathItem[];
}

const getListItemStyle = (hasChildren: boolean, level: number) =>
  hasChildren
    ? { paddingLeft: `${level}rem`, paddingRight: '1rem' }
    : { paddingLeft: `${level + 2}rem`, paddingRight: '1rem' };

const styles = {
  toggle: {
    cursor: 'pointer',
    marginRight: '0.5rem',
  },
};

const TreeItem = ({
  hasChildren,
  hasCategories,
  isAncestorSelected,
  level,
  tree,
  path,
}: Props) => {
  const {
    allAssetsSelected,
    autoSelectChildCategories,
    createFavorite,
    updateFavorite,
    disabledAssetIds,
    disabledCategoryIds,
    favoritesEnabled,
    favoriteMaterials,
    handleAssetClick,
    handleAssetToggle,
    handleCategoryToggle,
    handleToggle,
    onSelectAllAssetsToggle,
    open,
    rootTree,
    schemaId,
    selectedAssetIds,
    selectedCategoryId,
    selectedCategoryIds,
    showRootCategoryCheckbox,
    favoritesLists,
    handleAddList,
    canAddFavorite,
    canAddList,
    selectLeafCategory,
    dataCyPrefix,
  } = useContext(TreeContext);

  if (!rootTree) {
    return null;
  }

  const isRoot = tree.id === rootTree.id;

  const disabled =
    (!!schemaId && isAsset(tree) && tree.schemaId !== schemaId) ||
    //@ts-ignore
    tree?.inactive;

  const isAutoSelectedDescendant =
    autoSelectChildCategories && rootTree.id !== tree.id && isAncestorSelected;

  const selected =
    selectedAssetIds?.has(tree.id) ||
    selectedCategoryIds?.has(tree.id) ||
    (isAsset(tree) && allAssetsSelected);

  const enabledCheckbox = isAsset(tree)
    ? !!selectedAssetIds
    : !!selectedCategoryIds || (isRoot && !!onSelectAllAssetsToggle);

  const showCheckbox =
    isRoot && !isAsset(tree)
      ? showRootCategoryCheckbox
      : enabledCheckbox && !(selectLeafCategory && hasCategories);

  const checkboxDisabled =
    disabledAssetIds?.has(tree.id) ||
    disabledCategoryIds?.has(tree.id) ||
    isAutoSelectedDescendant ||
    (selectLeafCategory && hasChildren) ||
    (isAsset(tree) && allAssetsSelected);

  const listItemSelected = selectedCategoryId === tree.id;

  return (
    <ListItemButton
      disableGutters
      style={getListItemStyle(hasChildren, level)}
      selected={listItemSelected}
      disabled={disabled}
      data-cy={`${dataCyPrefix}inventoryFolder-${tree.name}`}
      onClick={
        isAsset(tree)
          ? () => handleAssetClick(tree)
          : () => handleToggle(tree, path)
      }
    >
      {hasChildren && (
        <Icon
          style={styles.toggle}
          icon={open.has(tree.id) ? ArrowDropDownIcon : ArrowRightIcon}
        />
      )}
      {showCheckbox && (
        <Checkbox
          checked={selected || isAutoSelectedDescendant}
          onClick={event =>
            isAsset(tree)
              ? handleAssetToggle(event, tree)
              : handleCategoryToggle(event, tree)
          }
          style={{ marginRight: '0.5rem' }}
          color="primary"
          disabled={checkboxDisabled}
        />
      )}
      <TreeRowIcon tree={tree} />
      <ListItemText
        primary={tree.name}
        secondary={isAsset(tree) || (!hasChildren && tree?.assetType)}
      />
      {favoritesEnabled && !hasChildren && showCheckbox && (
        <FavoritesIconButton
          item={tree}
          handleAddList={handleAddList}
          createFavorite={createFavorite}
          updateFavorite={updateFavorite}
          favorites={favoriteMaterials}
          favoritesLists={favoritesLists}
          typeName="items"
          canAddFavorite={canAddFavorite}
          canAddList={canAddList}
        />
      )}
    </ListItemButton>
  );
};

export default TreeItem;
